import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, generatePath, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import { upsertContactUser } from '../../common/fetch.js';
import {
  confirmMsg,
  dataSourcesForContactDetailsMsg,
  editMsg,
  moveContactToAnotherCompanyMsg,
  t,
} from '../../common/i18n';
import { isMultiBiz } from '../../common/utils/accountUtils';
import { isConflictingContactError, isDuplicateContactError } from '../../common/utils/contactUtils';
import { isFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { containsYttSubscription } from '../../common/utils/subscriptionUtils.js';
import { ContactStatus } from '../../generated/api/contactStatus.js';
import { ContactType } from '../../generated/api/contactType.js';
import { UserProfile } from '../../generated/api/userProfile.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { AccessRightsAccordion } from '../AccessRights/AccessRights.js';
import { BillingAccountsAccordion } from '../BillingAccountsAccordion/BillingAccountsAccordion.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { ContactForm } from '../ContactForm/ContactForm.js';
import { DuplicateContactDialog } from '../Dialogs/DuplicateContactDialog.js';
import { GenericInfoDialog } from '../Dialogs/GenericInfoDialog.js';
import { PendingActionsForContactDialog } from '../Dialogs/PendingActionsForContactDialog.js';
import {
  BroadbandSubscriptionsAccordion,
  DeviceSubscriptionsAccordion,
  ServiceSubscriptionsAccordion,
  VoiceSubscriptionsAccordion,
} from '../SubscriptionsAccordion/SubscriptionsAccordion.js';
import { UserRolesAndAdminRights } from '../UserRolesAndAdminRights/UserRolesAndAdminRights.js';
import { YttContactChangeDialog } from '../YttContactChangeDialog/YttContactChangeDialog';
import type { ContactDetailsProps } from './ContactDetails.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { DuplicateContactResponse } from '../../common/utils/contactUtils.js';
import type { Contact } from '../../generated/api/contact.js';
import type { ContactCommonFunction } from '../../generated/api/contactCommonFunction.js';
import type { ContactPerson } from '../../generated/api/contactPerson.js';
import type { GenericErrorDuplicateContact } from '../../generated/api/genericErrorDuplicateContact.js';
import type { PutContactResponse } from '../../generated/api/putContactResponse.js';
import type { ConfigState } from '../../public/site/configStore.js';

import './ContactDetails.scss';

type PersonFormValues = {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  phoneNumber: string | undefined;
  costCenter: string | undefined;
  employeeNumber: string | undefined;
};

type CommonFunctionFormValues = {
  name: string | undefined;
  email: string | undefined;
  phoneNumber: string | undefined;
};

type ContactFormValues = PersonFormValues | CommonFunctionFormValues;

interface ContactDetailsFormProps extends ContactDetailsProps {
  config: ConfigState;
  contact: Contact;
  mdmId: string;
}

interface DialogMessageProps {
  topParagraph: string;
  bottomParagraph?: string;
}

const DialogMessage = (props: DialogMessageProps) => {
  return (
    <>
      <div className={dsClass.PADDING_BOTTOM_4}>{props.topParagraph}</div>
      {props.bottomParagraph && <div className={dsClass.PADDING_BOTTOM_4}>{props.bottomParagraph}</div>}
    </>
  );
};

const getFormInitialValues = (contact: Contact): ContactFormValues => {
  if (contact.contactType === ContactType.COMMON_FUNCTION) {
    return {
      name: contact.commonFunction?.name,
      email: contact.commonFunction?.email,
      phoneNumber: contact.commonFunction?.phoneNumber,
    };
  } else {
    return {
      firstName: contact.person?.firstName,
      lastName: contact.person?.lastName,
      email: contact.person?.email,
      phoneNumber: contact.person?.phoneNumber,
      costCenter: contact.person?.costCenter ?? '',
      employeeNumber: contact.person?.employeeNumber ?? '',
    };
  }
};

// A function to generate correct contact payload. If the contactType is common function and also person
// object is sent, then SFDC returns an exception. Same for contactType of Person, commonFunction needs to be
// left out. Also, same happens if the value for either one is undefined.
// todo: Perhaps prune out the undefined person or common function in online-ui-api
const generateContactBasedOnContactType = (
  contact: Contact,
  contactStatus?: ContactStatus,
  formValues?: ContactFormValues,
  person?: ContactPerson
): Contact => {
  const { contactId, contactType } = contact;
  if (contactType === ContactType.COMMON_FUNCTION) {
    const commonFunction = formValues as ContactCommonFunction;
    return { contactId, contactType, contactStatus, commonFunction };
  } else {
    const personForUpdate = formValues ? { ...person, ...(formValues as ContactPerson) } : contact.person;
    return { contactId, contactType, contactStatus, person: personForUpdate };
  }
};

export const ContactDetailsForm = ({
  billingAccounts,
  broadbandSubscriptions,
  config,
  contact,
  contactMasterId,
  deviceSubscriptions,
  mdmId,
  pendingActionsResponse,
  serviceSubscriptions,
  voiceSubscriptions,
  invitation,
  serviceAgreementsResponse,
}: ContactDetailsFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticatedUser, elisaIdV3Enabled } = useAuth();
  const [isEdit, setIsEdit] = useState(!!location.state?.redirectToPath);
  const [isRemoveUserSubmitting, setIsRemoveUserSubmitting] = useState(false);
  const [pendingActionsDialogVisible, setPendingActionsDialogVisible] = useState(false);
  const [yttContactChangeDialogVisible, setYttContactChangeDialogVisible] = useState(false);
  const [duplicateContactDialogVisible, setDuplicateContactDialogVisible] = useState(false);
  const [contactChangeNotAllowedDialogVisible, setContactChangeNotAllowedDialogVisible] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState<GenericErrorDuplicateContact | undefined>(undefined);
  const [submitInProgress, setIsSubmitInProgress] = useState(false);
  const [submitYttContactInProgress, setSubmitYttContactInProgress] = useState(false);
  const [isShowDelayedBackNavigation, setIsShowDelayedBackNavigation] = useState(false);
  const contactType = contact.contactType || ContactType.PERSON;
  const { contactId, person } = contact;
  const hasBillingAccounts = billingAccounts.length > 0;
  const { search } = useLocation();

  const onShowDialog = (params: DialogParams) => openDialog(params);

  const removeUser = async (onCloseDialog: () => void) => {
    onCloseDialog();
    setIsRemoveUserSubmitting(true);
    try {
      await upsertContactUser(
        generateContactBasedOnContactType(contact, ContactStatus.PASSIVE),
        true,
        undefined,
        undefined,
        mdmId
      );
      setIsEdit(false);
      navigate(paths.COMPANY_INFO_CONTACTS);
    } catch (err) {
      addNotification(t.M2FC('Failed to remove contact'), 'error');
    } finally {
      setIsRemoveUserSubmitting(false);
    }
  };

  const showConfirmRemoveUserDefaultDialog = () => {
    onShowDialog({
      header: t.PWYT('Do you really want to remove user?'),
      confirmButtonText: t.MEIL('Remove user'),
      body: (
        <>
          <div className={dsClass.PADDING_BOTTOM_4}>
            {t.YEX6('User will be completely removed from the system. Operation cannot be undone.')}
          </div>
          <div className={dsClass.PADDING_BOTTOM_4}>
            {t.TCGR(
              `Please note that the user's association with the company will also be removed, and afterward, they won't be able to view their subscriptions or devices related to that company in Employee OmaElisa.`
            )}
          </div>
        </>
      ),
      onConfirm: removeUser,
      type: DialogType.GENERIC_CONFIRMATION_DIALOG,
    });
  };

  const showConfirmRemoveUserManagedInSalesforceDialog = () => {
    onShowDialog({
      header: t.FBDO('User has administrator rights'),
      confirmButtonText: t.MEIL('Remove user'),
      body: (
        <>
          <div className={dsClass.PADDING_BOTTOM_4}>
            {t.MSD6(
              'Please note that the user you are about to remove has administrator rights in OmaElisa for Companies, and these rights will also be removed during deletion.'
            )}
          </div>
        </>
      ),
      onConfirm: removeUser,
      type: DialogType.GENERIC_CONFIRMATION_DIALOG,
    });
  };

  const showUserNotManagedInSalesforceDialog = () => {
    onShowDialog({
      header: t.WNUM('User can not be removed'),
      buttonText: t.B2V1('Cancel'),
      body: (
        <div className={dsClass.PADDING_BOTTOM_4}>
          {t.OJDL(
            'The user can not be removed because they have administrator rights in OmaElisa for Companies. Please remove administrator rights in'
          )}{' '}
          <a href={`${config.classicSiteUrl}`}>{t.JS80('OmaElisa Classic')}</a>.
        </div>
      ),
      type: DialogType.GENERIC_INFO_DIALOG,
    });
  };

  const confirmRemoveContact = (userProfile?: string, userRightsInSalesforce?: boolean) => {
    if (userProfile === UserProfile.KEY_USER && userRightsInSalesforce) {
      showConfirmRemoveUserManagedInSalesforceDialog();
    } else if (userProfile === UserProfile.KEY_USER && !userRightsInSalesforce) {
      showUserNotManagedInSalesforceDialog();
    } else {
      showConfirmRemoveUserDefaultDialog();
    }
  };

  const removeContact = () => {
    if (hasBillingAccounts || contact.isPartOfBilling || contact.hasSubscriptions) {
      onShowDialog({
        header: t.WNUM('The user can not be removed'),
        buttonText: t.WOYD('Close'),
        body: (
          <DialogMessage
            topParagraph={t.VR6E(
              'The user can not be removed because they have active subscriptions or the user is a contact person or a recipient on a billing account.'
            )}
            bottomParagraph={t.WT42(
              'Please update subscriptions user details and/or billing accounts contact details.'
            )}
          />
        ),
        type: DialogType.GENERIC_INFO_DIALOG,
      });
    } else {
      confirmRemoveContact(contact.person?.userProfile, authenticatedUser?.userRightsInSalesforce);
    }
  };

  const handleSaveContactError = (responseData: PutContactResponse | DuplicateContactResponse, isYtt = false) => {
    if (isDuplicateContactError(responseData)) {
      setDuplicateContact((responseData as DuplicateContactResponse).duplicateContact);
      setDuplicateContactDialogVisible(true);
    } else if (isYtt && isConflictingContactError(responseData)) {
      setContactChangeNotAllowedDialogVisible(true);
      addNotification(t.O4KU('Updating contact details failed'), 'error');
    } else {
      addNotification(t.O4KU('Updating contact details failed'), 'error');
    }
  };

  const navigateBack = () => {
    // Delay the navigation to allow the user to realize what is happening
    const delay = location.state?.redirectDelayMs ?? 2000;
    window.scrollTo(0, 0);
    setIsShowDelayedBackNavigation(true);
    setTimeout(() => {
      setIsShowDelayedBackNavigation(false);
      navigate(location.state.redirectToPath, { replace: true });
    }, delay);
  };

  const saveContact = async (contactParam: Contact, forceUpsert: boolean, isYtt = false) => {
    try {
      const response = await upsertContactUser(contactParam, forceUpsert, false, true, mdmId);
      if (!response.ok) {
        const responseData = await response.json();
        handleSaveContactError(responseData, isYtt);
        return;
      }
      addNotification(t.EZ2Q('Contact details updated successfully'), 'success');
      if (location.state?.redirectToPath) {
        navigateBack();
      } else {
        navigate(paths.COMPANY_INFO_CONTACTS);
      }
    } catch {
      addNotification(t.O4KU('Updating contact details failed'), 'error');
    }
  };

  const methods = useForm({
    values: getFormInitialValues(contact),
    mode: 'onBlur',
  });

  const { handleSubmit, reset, getValues } = methods;

  const onReset = () => {
    if (location.state?.redirectToPath) {
      navigateBack();
    } else {
      reset();
      setIsEdit(false);
    }
  };

  const onSubmit = async (values: ContactFormValues) => {
    const isYtt = containsYttSubscription(serviceSubscriptions.map(res => res.result));
    const isPerson = contact.contactType === ContactType.PERSON && !!person;

    if (isPerson) {
      const personValues = values as PersonFormValues;
      const wholeNameChanged =
        contact.person?.firstName !== personValues.firstName && contact.person?.lastName !== personValues.lastName;
      const nameEmailOrPhoneChanged =
        contact.person?.firstName !== personValues.firstName ||
        contact.person?.lastName !== personValues.lastName ||
        contact.person?.email !== personValues.email ||
        contact.person?.phoneNumber !== personValues.phoneNumber;

      if (wholeNameChanged) {
        onShowDialog({
          contactPerson: person,
          values: personValues as ContactPerson,
          contactType,
          contactId,
          onConfirm: () => {
            setIsEdit(false);
          },
          type: DialogType.UPDATE_CONTACT_DUPLICATE,
        });
        return;
      }
      if (isYtt && nameEmailOrPhoneChanged) {
        setYttContactChangeDialogVisible(true);
        return;
      }
    }

    setIsSubmitInProgress(true);
    await saveContact(generateContactBasedOnContactType(contact, contact.contactStatus, values, person), false, isYtt);
    setIsSubmitInProgress(false);
  };

  const saveYttContact = async () => {
    setSubmitYttContactInProgress(true);
    await saveContact(
      generateContactBasedOnContactType(contact, contact.contactStatus, getValues(), person),
      true,
      true
    );
    setSubmitYttContactInProgress(false);
    setYttContactChangeDialogVisible(false);
  };

  const onEditClick = () => {
    if (pendingActionsResponse.hasPendingActions) {
      setPendingActionsDialogVisible(true);
    } else {
      setIsEdit(true);
    }
  };

  const onClosePendingActionsDialog = () => setPendingActionsDialogVisible(false);

  const handleBeforeEditUserRolesAndAdminRights = () => {
    if (pendingActionsResponse.hasPendingActions) {
      setPendingActionsDialogVisible(true);
      return false;
    }
    return true;
  };

  const DelayedNavigationModal = ({ description }: { description: string }) => {
    return (
      <CL.Modal
        className={dsClass.MARGIN_TOP_10}
        autoOpen
        content={<CL.Loader i18n_loader_description={description}></CL.Loader>}
        useBackdrop
        disableBodyScroll
        closeWithBackdropClick={false}
        onModalClose={() => {}}
      />
    );
  };

  return (
    <div className="of-contact-details__content">
      {isShowDelayedBackNavigation && <DelayedNavigationModal description={location.state?.redirectMessage ?? ''} />}
      {pendingActionsDialogVisible && <PendingActionsForContactDialog onCloseDialog={onClosePendingActionsDialog} />}
      {duplicateContactDialogVisible && duplicateContact && (
        <DuplicateContactDialog
          duplicateContact={duplicateContact}
          hideUseContactDuplicateButton={true}
          onCloseDialog={() => setDuplicateContactDialogVisible(false)}
          onSelectDuplicateContact={() => {}}
        />
      )}
      {contactChangeNotAllowedDialogVisible && (
        <GenericInfoDialog
          onCloseDialog={() => setContactChangeNotAllowedDialogVisible(false)}
          header={t.M3ZW('Mobile phone number or email cannot be updated')}
          body={
            <div>
              {t.L9OE(
                'Please contact our customer service at 010 80 8088 (Monday to Friday, 8:00 AM to 4:30 PM). We apologize for the inconvenience.'
              )}
            </div>
          }
        />
      )}
      {yttContactChangeDialogVisible && (
        <YttContactChangeDialog
          submitting={submitYttContactInProgress}
          onCloseDialog={() => setYttContactChangeDialogVisible(false)}
          onConfirm={saveYttContact}
        />
      )}
      <FormProvider {...methods}>
        <form>
          <ContactForm contact={contact} isEdit={isEdit} />
          {!isEdit && (
            <CL.Button
              data-testid="edit-contact-button"
              className="of-contact-details__editButton"
              type="button"
              color="light"
              onClick={onEditClick}
            >
              {t.NVPK(editMsg)}
            </CL.Button>
          )}
          {isEdit && (
            <div className="of-contact-details__options">
              <ButtonGroupForSubmitAndBack
                onCancel={onReset}
                onSubmit={handleSubmit(onSubmit)}
                submitButtonText={t.QVYK(confirmMsg)}
                submitting={submitInProgress}
                submitDisabled={isRemoveUserSubmitting}
                submitOnLeft={true}
              />
              {contact.person?.userName !== authenticatedUser?.userName && (
                <div>
                  <CL.Button
                    className="of-contact-details__removeButton"
                    color="light"
                    disabled={submitInProgress}
                    loading={isRemoveUserSubmitting}
                    onClick={removeContact}
                  >
                    {t.MEIL('Remove user')}
                  </CL.Button>
                </div>
              )}
            </div>
          )}
        </form>
      </FormProvider>
      {!isEdit &&
        !pendingActionsResponse.hasPendingActions &&
        contact.contactType === ContactType.PERSON &&
        isMultiBiz(authenticatedUser) && (
          <Link to={generatePath(paths.COMPANY_INFO_CONTACT_MOVE, { contactMasterId }) + search}>
            <div className={`of-contact-details__move-contact ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}>
              <CL.Icon icon="switch" size="s" color="blue-600" />
              <span className={dsClass.MARGIN_LEFT_2}>{t.OQ8K(moveContactToAnotherCompanyMsg)}</span>
            </div>
          </Link>
        )}
      {elisaIdV3Enabled && (
        <AccessRightsAccordion
          contact={contact}
          invitation={invitation}
          serviceAgreementsResponse={serviceAgreementsResponse}
          onBeforeEdit={handleBeforeEditUserRolesAndAdminRights}
        />
      )}
      {!elisaIdV3Enabled &&
        (isFeatureEnabled(config.featureFlags.showUserRightsAccordion) ||
          authenticatedUser?.enabledFeatureFlags?.includes('showUserRightsAccordion')) &&
        contact.contactType !== ContactType.COMMON_FUNCTION && (
          <UserRolesAndAdminRights
            ignoreAccordion={false}
            isNewDisconnectedContact={false}
            contact={contact}
            onShowDialog={onShowDialog}
            authenticatedUser={authenticatedUser!}
            mdmId={mdmId}
            onBeforeEdit={handleBeforeEditUserRolesAndAdminRights}
          />
        )}
      <DeviceSubscriptionsAccordion subscriptions={deviceSubscriptions} />
      <VoiceSubscriptionsAccordion subscriptions={voiceSubscriptions} />
      <BroadbandSubscriptionsAccordion subscriptions={broadbandSubscriptions} />
      <ServiceSubscriptionsAccordion subscriptions={serviceSubscriptions} />
      {hasBillingAccounts && <BillingAccountsAccordion contact={contact} billingAccounts={billingAccounts} />}
      <div className="of-contact-details__disclaimertext ea-disclaimertext ea-text--grey">
        {t.U3UL(dataSourcesForContactDetailsMsg)}
      </div>
    </div>
  );
};
